import { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";

// ✅ use your existing base url / headers util
const BASE_URL = "https://nissi-india.com";

function axiosHeaders() {
  const loginuserid =
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    localStorage.getItem("userid") ||
    "";
  return {
    "Content-Type": "application/json",
    "x-loginuserid": loginuserid,
  };
}

// ---------- date helpers ----------
function pad2(n) {
  return String(n).padStart(2, "0");
}
function toDDMMYYYY(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
}
function normalizeDDMMYYYY(input) {
  // allow dd/MM/yyyy or ISO or empty
  const s = String(input || "").trim();
  if (!s) return "";
  // already dd/MM/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) return toDDMMYYYY(dt);

  return s; // fallback (server will validate)
}

// ---------- branch normalize ----------
const normalizeBranches = (arr) => {
  return (arr || []).map((b) => ({
    id: b.fld_Branchid ?? b.fld_branchid ?? b.id ?? b.value ?? b.Value,
    name: b.fld_BranchName ?? b.fld_branchname ?? b.name ?? b.text ?? b.Text,
  }));
};

export default function EmployeeTransferApprovalEntry() {
  const navigate = useNavigate();
  const { employeeTransferId } = useParams();

  const { theme } = useContext(ThemeContext);
  const skin = useMemo(() => getCurrentSkin(theme), [theme]);

  // ---- session ----
  const finYearId = localStorage.getItem("finYearId") || localStorage.getItem("FinYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  // ---- meta ----
  const [toSites, setToSites] = useState([{ id: "Select", name: "Select" }]);

  // ---- loading ----
  const [loading, setLoading] = useState(false);

  // ---- form state ----
  const [transferCode, setTransferCode] = useState("");
  const [mode, setMode] = useState("Transfer");

  const [employeeId, setEmployeeId] = useState(""); // important for dutycard update
  const [employeeCode, setEmployeeCode] = useState("");
  const [employeeName, setEmployeeName] = useState("");

  const [designation, setDesignation] = useState("");
  const [department, setDepartment] = useState("");

  const [fromSiteId, setFromSiteId] = useState("Select");
  const [toSiteId, setToSiteId] = useState("Select");

  const [effectDate, setEffectDate] = useState("");
  const [paidUpto, setPaidUpto] = useState("");

  const [dailyBata, setDailyBata] = useState("");
  const [monthlySalary, setMonthlySalary] = useState("");
  const [travelAdvance, setTravelAdvance] = useState("");
  const [canteenDue, setCanteenDue] = useState("");
  const [debitTo, setDebitTo] = useState("");

  const [reason, setReason] = useState("");

  const [workedFrom, setWorkedFrom] = useState("");
  const [workedTo, setWorkedTo] = useState("");
  const [leaveDays, setLeaveDays] = useState("");
  const [lop, setLop] = useState("");

  // ---- load meta (for ToSite dropdown, HO shows all) ----
  const loadMeta = async () => {
    if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");
    if (!String(branchId).trim()) return toast.warn("branchId missing.");

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/employee-transfer-entry/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: String(branchType || "HO"),
        },
        headers: axiosHeaders(),
        timeout: 60000,
      });

      if (res?.data?.success) {
        const ts = normalizeBranches(res.data.toSites || []);
        setToSites([{ id: "Select", name: "Select" }, ...ts]);
      } else {
        toast.error(res?.data?.message || "Meta load failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed");
    } finally {
      setLoading(false);
    }
  };

  // ---- load entry ----
  const loadEntry = async () => {
    if (!employeeTransferId) return toast.error("employeeTransferId missing in URL");
    if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/employee-transfer-approval/entry/${employeeTransferId}`, {
        params: { finYearId: Number(finYearId) },
        headers: axiosHeaders(),
        timeout: 60000,
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Entry load failed");
        return;
      }

      const d = res.data.data || {};

      // IMPORTANT: ensure node returns these fields (see note below)
      setEmployeeId(String(d.employeeId ?? d.fld_employee_employeeid ?? ""));
      setEmployeeCode(String(d.employeeCode ?? d.code ?? ""));
      setEmployeeName(String(d.employeeName ?? d.empname ?? ""));

      setTransferCode(String(d.transferCode ?? d.fld_employee_transferemployeecode ?? ""));
      setMode(String(d.mode ?? d.tpe ?? "Transfer"));

      setDesignation(String(d.designation ?? d.designme ?? ""));
      setDepartment(String(d.department ?? d.depnme ?? ""));

      setFromSiteId(String(d.fromSiteId ?? d.fld_employee_frombranchid ?? "Select"));
      setToSiteId(String(d.toSiteId ?? d.fld_employee_tobranchid ?? "Select"));

      setEffectDate(normalizeDDMMYYYY(d.effectDate ?? d.effectdate ?? d.fld_employee_effectdate));
      setPaidUpto(normalizeDDMMYYYY(d.paidUpto ?? d.paidupto ?? d.fld_employee_batapaidupto));

      setDailyBata(String(d.dailyBata ?? d.bata ?? d.fld_bata ?? ""));
      setMonthlySalary(String(d.monthlySalary ?? d.salary ?? d.fld_grosssalary ?? ""));
      setTravelAdvance(String(d.travelAdvance ?? d.travel ?? d.fld_traveladvance ?? ""));
      setCanteenDue(String(d.canteenDue ?? d.canteendue ?? d.fld_employee_canteendue ?? ""));
      setDebitTo(String(d.debitTo ?? d.debit ?? d.fld_employee_debitto ?? ""));

      setReason(String(d.reason ?? d.fld_reasonfor ?? ""));

      setWorkedFrom(normalizeDDMMYYYY(d.workedFrom ?? d.wrkdfrom ?? d.fld_wrkdperiodfrom));
      setWorkedTo(normalizeDDMMYYYY(d.workedTo ?? d.wrkdto ?? d.fld_wrkdperiodto));
      setLeaveDays(String(d.leaveDays ?? d.leavedays ?? d.fld_leavedays ?? ""));
      setLop(String(d.lop ?? d.fld_lopdays ?? ""));
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Entry load failed");
    } finally {
      setLoading(false);
    }
  };

  // ---- validations (same intent as ASP) ----
  const validateCore = () => {
    if (!employeeCode || employeeCode === "Select") return toast.warn("Select Employee Code"), false;
    if (!employeeName || employeeName === "Select") return toast.warn("Select Employee Name"), false;
    if (!fromSiteId || fromSiteId === "Select") return toast.warn("Please select From Site.."), false;
    if (!toSiteId || toSiteId === "Select") return toast.warn("Please select To Site.."), false;
    if (!effectDate) return toast.warn("Transfer Effective Date required"), false;
    if (!reason.trim()) return toast.warn("Please Type the Reason"), false;
    return true;
  };

  // ---- approve / disapprove ----
  const handleApprove = async () => {
    if (!validateCore()) return;

    try {
      setLoading(true);
      const res = await axios.put(
        `${BASE_URL}/api/payroll/employee-transfer-approval/approve/${employeeTransferId}`,
        {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          employeeTransferId: Number(employeeTransferId),

          // required server-side for dutycard update
          employeeId: Number(employeeId),

          // approval needs toSite + effectDate
          toSiteId: Number(toSiteId),
          effectDate: effectDate, // dd/MM/yyyy
        },
        { headers: axiosHeaders(), timeout: 60000 }
      );

      if (res?.data?.success) {
        toast.success(res.data.message || "Approved Successfully..");
        setTimeout(() => navigate("/PayRoll/EmployeeTransferApprovalListing"), 600);
      } else {
        toast.error(res?.data?.message || "Approve failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Approve failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDisapprove = async () => {
    try {
      setLoading(true);
      const res = await axios.put(
        `${BASE_URL}/api/payroll/employee-transfer-approval/disapprove/${employeeTransferId}`,
        {
          finYearId: Number(finYearId),
          employeeTransferId: Number(employeeTransferId),
        },
        { headers: axiosHeaders(), timeout: 60000 }
      );

      if (res?.data?.success) {
        toast.success(res.data.message || "Disapproved Successfully..");
        setTimeout(() => navigate("/PayRoll/EmployeeTransferApprovalListing"), 600);
      } else {
        toast.error(res?.data?.message || "Disapprove failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Disapprove failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    loadEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeTransferId]);

  const inputBase =
    "w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-offset-0";
  const labelBase = "block text-sm font-semibold mb-1";
  const readonlyCls = "bg-gray-100 cursor-not-allowed";

  return (
    <PayRollLayoutWrapper>
      <ToastContainer />
      <HeaderBar />

      <div className="p-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-lg font-bold" style={{ color: skin?.head || "#111" }}>
              Employee Transfer Approval Entry
            </div>
            <div className="text-xs opacity-70">INFRA SCREEN : C-25/1</div>
          </div>

          <button
            type="button"
            onClick={() => navigate("/PayRoll/EmployeeTransferApprovalListing")}
            className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>

        <div className="rounded-xl border bg-white p-4 shadow-sm">
          {loading && (
            <div className="mb-3 text-sm opacity-70">
              Loading...
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Transfer Number */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Transfer Number</label>
              <input className={`${inputBase} ${readonlyCls}`} value={transferCode} readOnly />
            </div>

            {/* Mode */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Mode</label>
              <div className="flex gap-4 mt-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={mode === "Camp"}
                    onChange={() => setMode("Camp")}
                    disabled
                  />
                  Camp
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={mode !== "Camp"}
                    onChange={() => setMode("Transfer")}
                    disabled
                  />
                  Transfer
                </label>
              </div>
            </div>

            {/* Employee Code */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Employee Code</label>
              <input className={`${inputBase} ${readonlyCls}`} value={employeeCode} readOnly />
            </div>

            {/* Effective Date */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Transfer Effective Date *</label>
              <input
                className={`${inputBase} ${readonlyCls}`}
                value={effectDate}
                readOnly
              />
            </div>

            {/* Employee Name */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Employee Name</label>
              <input className={`${inputBase} ${readonlyCls}`} value={employeeName} readOnly />
            </div>

            {/* Designation */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Designation</label>
              <input className={`${inputBase} ${readonlyCls}`} value={designation} readOnly />
            </div>

            {/* From Site */}
            <div className="lg:col-span-3">
              <label className={labelBase}>From Site</label>
              <input className={`${inputBase} ${readonlyCls}`} value={String(fromSiteId)} readOnly />
            </div>

            {/* Department */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Department</label>
              <input className={`${inputBase} ${readonlyCls}`} value={department} readOnly />
            </div>

            {/* To Site (keep enabled if you want approver to change it) */}
            <div className="lg:col-span-3">
              <label className={labelBase}>To Site *</label>
              <select className={inputBase} value={toSiteId} onChange={(e) => setToSiteId(e.target.value)}>
                {toSites.map((b) => (
                  <option key={`${b.id}`} value={String(b.id)}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Daily Bata */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Daily Bata</label>
              <input className={`${inputBase} ${readonlyCls}`} value={dailyBata} readOnly />
            </div>

            {/* Monthly Salary */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Monthly Salary</label>
              <input className={`${inputBase} ${readonlyCls}`} value={monthlySalary} readOnly />
            </div>

            {/* Travel Advance */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Travelling Advance</label>
              <input className={`${inputBase} ${readonlyCls}`} value={travelAdvance} readOnly />
            </div>

            {/* Canteen Due */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Canteen Due</label>
              <input className={`${inputBase} ${readonlyCls}`} value={canteenDue} readOnly />
            </div>

            {/* Bata Paid Upto */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Bata Paid Upto</label>
              <input className={`${inputBase} ${readonlyCls}`} value={paidUpto} readOnly />
            </div>

            {/* Debit To */}
            <div className="lg:col-span-3">
              <label className={labelBase}>Debit To</label>
              <input className={`${inputBase} ${readonlyCls}`} value={debitTo} readOnly />
            </div>

            {/* Reason */}
            <div className="lg:col-span-6">
              <label className={labelBase}>Reason for Transfer *</label>
              <input className={`${inputBase} ${readonlyCls}`} value={reason} readOnly />
            </div>
          </div>

          {/* Worked Period */}
          <div className="mt-5 rounded-lg border p-3">
            <div className="font-semibold text-sm mb-3">Worked Period</div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-3">
                <label className={labelBase}>From Date</label>
                <input className={`${inputBase} ${readonlyCls}`} value={workedFrom} readOnly />
              </div>

              <div className="lg:col-span-3">
                <label className={labelBase}>To Date</label>
                <input className={`${inputBase} ${readonlyCls}`} value={workedTo} readOnly />
              </div>

              <div className="lg:col-span-3">
                <label className={labelBase}>Total Leave Days</label>
                <input className={`${inputBase} ${readonlyCls}`} value={leaveDays} readOnly />
              </div>

              <div className="lg:col-span-3">
                <label className={labelBase}>Loss of Pay</label>
                <input className={`${inputBase} ${readonlyCls}`} value={lop} readOnly />
              </div>
            </div>
          </div>

          {/* actions */}
          <div className="mt-5 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={handleApprove}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md bg-green-600 text-white px-4 py-2 text-sm disabled:opacity-60"
            >
              <CheckCircle2 size={16} />
              Approve
            </button>

            <button
              type="button"
              onClick={handleDisapprove}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md bg-red-600 text-white px-4 py-2 text-sm disabled:opacity-60"
            >
              <XCircle size={16} />
              Disapprove
            </button>
          </div>

          {/* hidden info helpful for debug */}
          <div className="mt-3 text-xs opacity-60 text-center">
            employeeTransferId: {employeeTransferId} | employeeId: {employeeId}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
}
